"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "motion/react";
import { Breakpoint, useCurrentBreakpoint } from "@/lib/hooks/useBreakpoint";
import { useIsMobile } from "@/lib/hooks";
import { Image, ImageProps } from "@/components/ui/Image";

interface BlobCursorProps {
  containerRef: React.RefObject<HTMLElement | null>;
  targets: { selector: string; image: ImageProps }[];
  /** 0 = snapped to target center, 1 = follows mouse exactly. Default: 1 */
  cursorPull?: number;
}

export const BlobCursor: React.FC<BlobCursorProps> = ({
  containerRef,
  targets,
  cursorPull = 0.3,
}) => {
  const isMobile = useIsMobile();
  const breakpoint = useCurrentBreakpoint();
  const responsiveDiameter: Record<Breakpoint, number> = {
    xs: 170,
    sm: 180,
    md: 200,
    lg: 260,
    xl: 300,
    "2xl": 360,
  };
  const diameter = responsiveDiameter[breakpoint] ?? 100;
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [previousImage, setPreviousImage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  const activeCenterRef = useRef<{ x: number; y: number } | null>(null);
  const currentImageRef = useRef<string | null>(null);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, { stiffness: 80, damping: 20, mass: 0.5 });
  const y = useSpring(rawY, { stiffness: 80, damping: 20, mass: 0.5 });

  // Keep ref in sync to avoid stale closures in event listeners
  useEffect(() => {
    currentImageRef.current = currentImage;
  }, [currentImage]);

  // Mouse tracking
  useEffect(() => {
    const element = containerRef.current;
    if (isMobile || !element) return;
    const onMove = (e: MouseEvent) => {
      const containerRect = element.getBoundingClientRect();
      const center = activeCenterRef.current;
      if (center) {
        rawX.set(
          center.x - containerRect.left + (e.clientX - center.x) * cursorPull
        );
        rawY.set(
          center.y - containerRect.top + (e.clientY - center.y) * cursorPull
        );
      } else {
        rawX.set(e.clientX - containerRect.left);
        rawY.set(e.clientY - containerRect.top);
      }
    };
    element.addEventListener("mousemove", onMove);
    return () => element.removeEventListener("mousemove", onMove);
  }, [isMobile, rawX, rawY]);

  // Hover listeners
  useEffect(() => {
    if (isMobile) return;
    const cleanups: (() => void)[] = [];
    targets.forEach(({ selector, image }) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        const enter = () => {
          const rect = element.getBoundingClientRect();
          activeCenterRef.current = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
          setPreviousImage(currentImageRef.current);
          setCurrentImage(image.src);
          setVisible(true);
        };
        const leave = () => {
          activeCenterRef.current = null;
          setVisible(false);
        };
        element.addEventListener("mouseenter", enter);
        element.addEventListener("mouseleave", leave);
        cleanups.push(() => {
          element.removeEventListener("mouseenter", enter);
          element.removeEventListener("mouseleave", leave);
        });
      });
    });
    return () => cleanups.forEach((fn) => fn());
  }, [isMobile, targets]);

  return (
    <motion.div
      aria-hidden
      style={{
        x,
        y,
        translateX: "-50%",
        translateY: "-50%",
        width: diameter,
        height: diameter,
      }}
      initial={{ opacity: 0, scale: 0.2 }}
      animate={{
        scale: visible ? 1 : 0.2,
        opacity: visible ? 1 : 0,
        transition: {
          ease: visible ? "easeOut" : "easeIn",
          delay: visible ? 0 : 0.1,
        },
      }}
      transition={{ scale: { duration: 0.9 }, opacity: { duration: 0.2 } }}
      className="absolute top-0 left-0 pointer-events-none z-0 will-change-transform rounded-full overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden bg-black">
        {previousImage && (
          <motion.div
            key={"previous-image-" + previousImage}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 bg-cover bg-center"
          >
            <Image
              className="size-full rounded-[inherit] object-cover"
              src={previousImage}
              alt=""
              width={diameter}
              height={diameter}
            />
          </motion.div>
        )}
        {currentImage && (
          <motion.div
            key={"current-image-" + currentImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 bg-cover bg-center"
          >
            <Image
              className="size-full rounded-[inherit] object-cover"
              src={currentImage}
              alt=""
              width={diameter}
              height={diameter}
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

type BlobCursorContainerProps = Omit<BlobCursorProps, "containerRef"> &
  React.PropsWithChildren;

export const BlobCursorContainer: React.FC<BlobCursorContainerProps> = ({
  children,
  ...props
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  return (
    <div ref={ref} className="relative size-full">
      <BlobCursor containerRef={ref} {...props} />
      {children}
    </div>
  );
};
